import { useState } from 'react';
import { useApp } from '../store';
import { can } from '../permissions';
import { howFar, readPolicy, stateOf, usesSlots } from '../signoff-policy';
import { changeRequestFrom, sameScope, STANDARD_FORM } from '../change-request';
import type { Sprint } from '../sprints';
import { useSampleInert } from './SampleStrip';
import SignoffSheet from './SignoffSheet';

/**
 * ── APPROVAL OF A FINISHED FORTNIGHT — #332 ─────────────────────────────────
 *
 * Sign-off lived in Documents, so a team with no documents could not record an
 * approval at all — and the commonest thing anybody wants to approve is a
 * fortnight of finished work. Vinnie, 1 September 2026: *"If you have no
 * documents but you have an audit trail of sign-offs for those that have access
 * within the app then there would be no use for the document."*
 *
 * So it is asked for and answered HERE, on the thing it is about. The signature
 * table is the same one releases and documents use — one way to answer a row,
 * not a third.
 */
export default function SprintSignoff({ sprint }: { sprint: Sprint }) {
  const signoffs = useApp((s) => s.signoffs);
  const documents = useApp((s) => s.documents);
  const issues = useApp((s) => s.issues);
  const issueTypes = useApp((s) => s.issueTypes);
  const meta = useApp((s) => s.meta);
  const request = useApp((s) => s.requestSprintSignoff);
  const sprintSignoffDoc = useApp((s) => s.sprintSignoffDoc);
  const supersede = useApp((s) => s.supersedeDocument);
  const drillTo = useApp((s) => s.drillTo);
  const role = useApp((s) => s.role);
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState('');

  const mayAskC = can(role, 'editSettings');
  const inert = useSampleInert();

  /*
   * ── ONE LEDGER, TWO PHASES: AUTHORISE, THEN DELIVER — the redesign ─────────
   *
   * Vinnie, 6 September 2026. A slots sprint is signed off through documents,
   * not a checklist, and there are two of them, at the two ends of the work:
   *
   *   AUTHORISATION — a change request, raised at PLANNING. It signs the plan:
   *   "do this work". Available from the moment the sprint exists, because
   *   authorising is the thing you do before starting, not after.
   *
   *   DELIVERABLES — signed at the END, once the sprint is finished. It signs
   *   what was actually delivered, against a set of cards that has stopped
   *   moving — which is why this one waits for the close and authorisation does
   *   not. [[Sprints.tsx]]'s "a signature against a changing set of cards is not
   *   evidence" is exactly this: it is true of deliverables, not of the plan.
   *
   * Each is one document the developer edits and sends; clicking opens the
   * document itself, so there is one place the sign-off actually happens and
   * nobody hunts for it. A rank board is untouched below — #332's inline sheet.
   */
  if (usesSlots(readPolicy(meta.signoffPolicy))) {
    const docFor = (kind: 'change' | 'deliverables') =>
      documents.find((d) => d.kind === kind && d.sprint === sprint.id && d.state !== 'superseded');

    const statusOf = (doc: ReturnType<typeof docFor>): { text: string; bad: boolean } => {
      if (!doc) return { text: 'Not started', bad: false };
      if (doc.state === 'draft') return { text: 'Being prepared — not sent for sign-off yet', bad: false };
      if (doc.state === 'signed') return { text: 'Signed off', bad: false };
      const rows = signoffs.filter((r) => r.documentId === doc.id);
      const st = stateOf(rows);
      const far = howFar(rows);
      if (st === 'rejected') return { text: 'Not approved — somebody said no', bad: true };
      if (st === 'approved') return { text: 'Signed off', bad: false };
      return { text: `Awaiting sign-off · ${far.signed} of ${far.of} signed`, bad: false };
    };

    const act = async (kind: 'change' | 'deliverables') => {
      const doc = docFor(kind);
      if (doc) return drillTo('documents', doc.id);
      setBusy(kind);
      setProblem('');
      const { id, problem: why } = await sprintSignoffDoc(sprint.id, kind);
      setBusy('');
      if (why) return setProblem(why);
      if (id) drillTo('documents', id);
    };

    /* Raising a new version because the scope moved: supersede the signed one
       and open the fresh draft, which reads the board as it stands now. */
    const reauthorise = async (doc: ReturnType<typeof docFor>) => {
      if (!doc) return;
      setBusy(`re:${doc.kind}`);
      setProblem('');
      const { id, problem: why } = await supersede(doc.id);
      setBusy('');
      if (why) return setProblem(why);
      if (id) drillTo('documents', id);
    };

    /*
     * ── HAS THE SPRINT MOVED SINCE IT WAS SIGNED — Vinnie, 7 September 2026 ────
     *
     * "warn on the sprint": a signed document is frozen (0088), so once a sprint
     * is authorised the document keeps showing the cards as they were approved —
     * but the sprint's cards can still be changed. When they have, the signed
     * authorisation no longer covers the work, and the honest fix is a new one.
     * Said here rather than enforced: work legitimately discovers cards, and a
     * hard lock on a live sprint would stop that. The warning does the pushing.
     */
    const scopeNow = changeRequestFrom(
      issues.filter((i) => i.sprint === sprint.id),
      STANDARD_FORM,
      issueTypes
    );
    const drifted = (doc: ReturnType<typeof docFor>): boolean =>
      doc?.state === 'signed' && !!doc.scope && !sameScope(scopeNow, doc.scope);

    const finished = sprint.state === 'closed';
    const PHASES: {
      kind: 'change' | 'deliverables';
      label: string;
      make: string;
      why: string;
      ready: boolean;
      waitingNote?: string;
    }[] = [
      {
        kind: 'change',
        label: 'Authorisation',
        make: 'Create the change request',
        why: 'Signs the plan before the work starts — the change request the client approves.',
        ready: true,
      },
      {
        kind: 'deliverables',
        label: 'Deliverables',
        make: 'Create the deliverables sign-off',
        why: 'Signs what was actually delivered, once the sprint is finished.',
        ready: finished,
        waitingNote: 'Available once the sprint is finished.',
      },
    ];

    return (
      <>
        <div className="grp-heading">Sign-off</div>
        <div className="sp-phases">
          {PHASES.map((p) => {
            const doc = docFor(p.kind);
            const st = statusOf(doc);
            return (
              <div className="sp-phase" key={p.kind}>
                <div className="sp-phase-head">
                  <span className="sp-phase-name">{p.label}</span>
                  <span className={`sp-phase-state ${st.bad ? 'is-bad' : ''} ${doc?.state === 'signed' ? 'is-done' : ''}`}>
                    {st.text}
                  </span>
                </div>
                <div className="doc-act">
                  {doc ? (
                    <button className="btn btn-small" onClick={() => void act(p.kind)}>
                      Open →
                    </button>
                  ) : p.ready ? (
                    mayAskC && (
                      <button
                        disabled={busy === p.kind}
                        {...inert('btn btn-small btn-primary', () => void act(p.kind))}
                      >
                        {busy === p.kind ? 'Creating…' : p.make}
                      </button>
                    )
                  ) : (
                    <span className="doc-hint">{p.waitingNote}</span>
                  )}
                  {(doc || (p.ready && mayAskC)) && <span className="doc-hint">{p.why}</span>}
                </div>
                {drifted(doc) && (
                  <div className="sp-phase-drift" role="status">
                    <p>
                      The sprint&rsquo;s cards have changed since this was signed. The document still
                      shows what was approved — raise a new {p.label.toLowerCase()} to cover the
                      change.
                    </p>
                    {mayAskC && (
                      <button
                        disabled={busy === `re:${p.kind}`}
                        {...inert('btn btn-small btn-primary', () => void reauthorise(doc))}
                      >
                        {busy === `re:${p.kind}`
                          ? 'Creating…'
                          : p.kind === 'change'
                            ? 'Re-authorise'
                            : 'Redo the deliverables'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {problem && (
          <p className="hint problem" role="alert">
            {problem}
          </p>
        )}
      </>
    );
  }

  const rows = signoffs.filter((r) => r.sprintId === sprint.id);
  /* THE SAME TWO GATES AS SignOff.tsx, deliberately. Asking for approval and
     recording somebody else's answer are two different permissions, and a
     sprint must not answer that question differently from a release. */
  const mayAsk = can(role, 'editSettings');
  const mayActFor = can(role, 'answerForSomebody');

  const state = stateOf(rows);
  const far = howFar(rows);

  /*
   * ASKED BUT WITH NO ROWS is a real state and it is not "nothing here". It
   * means the raise failed halfway — the date is written last precisely so
   * this is visible rather than silent — and somebody has to know to look.
   */
  if (sprint.signoffRequestedAt && rows.length === 0)
    return (
      <div className="grid-warning" role="status">
        This sprint was sent for approval, but nobody was recorded as being asked. Nothing was
        signed. Check who signs work off in Settings, then ask again.
      </div>
    );

  if (!sprint.signoffRequestedAt)
    return (
      <>
        <div className="grp-heading">Approval</div>
        <p className="hint">
          Nobody has been asked to approve {sprint.name}.
          {mayAsk ? '' : ' Ask a project manager to send it.'}
        </p>
        {mayAsk && (
          <button
            disabled={busy === 'rank'}
            {...inert('btn btn-small btn-primary', async () => {
              setBusy('rank');
              setProblem('');
              const wrong = await request(sprint.id);
              setBusy('');
              if (wrong) setProblem(wrong);
            })}
          >
            {busy === 'rank' ? 'Asking…' : 'Send for approval'}
          </button>
        )}
        {/* The rule being empty is the commonest refusal and it names its own
            fix, so it is shown as it comes back rather than reworded here. */}
        {problem && (
          <p className="hint problem" role="alert">
            {problem}
          </p>
        )}
      </>
    );

  return (
    <>
      <div className="grp-heading">
        Approval ·{' '}
        {state === 'approved'
          ? 'Approved'
          : state === 'rejected'
            ? 'Not approved'
            : `Waiting · ${far.signed} of ${far.of}`}
      </div>
      {/*
        ⚠️ EVERY ONE OF THEM, said out loud rather than left to be inferred from
        a count. Vinnie chose this over "any one of them" on 3 September 2026:
        a sheet with two unsigned rows that still read as approved is how a
        sign-off stops being evidence.
      */}
      {state === 'waiting' && far.of > 1 && (
        <p className="hint">All {far.of} have to answer before this counts as approved.</p>
      )}
      {state === 'rejected' && (
        <p className="hint">
          Somebody has said no, so this is not approved whatever the rest answer.
        </p>
      )}
      {/*
        ── ONE TABLE PER STEP OR STAGE, IN THE ORDER THEY ARE ASKED — #346 ────
        A sheet of six names cannot show that three of them are not asked until
        the first three have answered. Grouped, it can. A stage sheet groups by
        its board-defined stages; a rank chain by its steps; a plain approval
        (and every board today has one) is drawn flat with no heading. The whole
        render is shared with the release and document sheets — see SignoffSheet.
        `groupLegacyChain` keeps this sheet's #346 rank-chain grouping, which the
        release and document sheets have never had and do not gain. */}
      <SignoffSheet
        rows={rows}
        policy={meta.signoffPolicy}
        mayActFor={mayActFor}
        maySendLink
        groupLegacyChain
      />
    </>
  );
}
